import { Component, ChangeDetectionStrategy, inject, signal, computed, effect, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  CardBodyComponent,
  CardComponent,
  CardHeaderComponent,
  ColComponent,
  ContainerComponent,
  RowComponent,
  TableDirective
} from '@coreui/angular-pro';
import { TranslatePipe, TranslateService } from 'i18n';
import { Logger } from 'shared';
import { UserService } from 'app';
import { EarningsStatisticsResponse, EarningsStatisticsPeriodData, EarningsStatisticsLevelData } from 'shared';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

type PeriodKey = 'total' | 'day_90' | 'day_30' | 'day_14' | 'day_7' | 'day_1';

@Component({
  selector: 'app-earnings-statistics-page',
  standalone: true,
  imports: [
    CommonModule,
    CardComponent,
    CardHeaderComponent,
    CardBodyComponent,
    ContainerComponent,
    RowComponent,
    ColComponent,
    TableDirective,
    TranslatePipe
  ],
  templateUrl: './earnings-statistics-page.component.html',
  styleUrls: ['./earnings-statistics-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EarningsStatisticsPageComponent {
  private userService = inject(UserService);
  private translate = inject(TranslateService);
  private destroyRef = inject(DestroyRef);
  private logger = new Logger('EarningsStatisticsPageComponent');

  // ────────────────────────────────────────────────
  // State signals
  // ────────────────────────────────────────────────
  statisticsData = signal<EarningsStatisticsResponse | null>(null);
  loading = signal(false);
  error = signal<string | null>(null);

  // ────────────────────────────────────────────────
  // Computed properties
  // ────────────────────────────────────────────────
  periods = computed(() => {
    const data = this.statisticsData();
    if (!data) return [];
    return data.periods as Array<
      EarningsStatisticsPeriodData & { period: PeriodKey }
    >;
  });

  activeLevels = computed(() => {
    const data = this.statisticsData();
    if (!data) return [];

    const levelsWithData = new Set<number>();

    data.periods.forEach(period => {
      Object.entries(period.levels).forEach(([levelStr, levelData]) => {
        if (levelData.total > 0) {
          levelsWithData.add(Number(levelStr));
        }
      });
    });

    return Array.from(levelsWithData).sort((a, b) => a - b);
  });

  // Computed version (reactive to language changes)
  periodLabels = computed<Record<PeriodKey, string>>(() => ({
    total: this.translate.translate('earningsStatistics.periods.total'),
    day_90: this.translate.translate('earningsStatistics.periods.last90Days'),
    day_30: this.translate.translate('earningsStatistics.periods.last30Days'),
    day_14: this.translate.translate('earningsStatistics.periods.last14Days'),
    day_7: this.translate.translate('earningsStatistics.periods.last7Days'),
    day_1: this.translate.translate('earningsStatistics.periods.last24Hours')
  }));


  // ────────────────────────────────────────────────
  // Effects
  // ────────────────────────────────────────────────
  constructor() {
    // Load data on init
    this.loadStatistics();

    // Reload on language change
    effect((onCleanup) => {
      const sub = this.translate.langChanges.subscribe(() => {
        this.logger.debug('Language changed, reloading statistics');
        this.loadStatistics();
      });
      // Cleanup subscription when effect is destroyed
      onCleanup(() => sub.unsubscribe());
    });
  }

  // ────────────────────────────────────────────────
  // Core actions
  // ────────────────────────────────────────────────
  loadStatistics(): void {
    this.loading.set(true);
    this.error.set(null);

    this.userService.getEarningsStatistics()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: data => {
            this.logger.debug('Earnings statistics loaded successfully', data);
            this.statisticsData.set(data);
            this.loading.set(false);
          },
          error: err => {
            this.logger.error('Error loading earnings statistics', err);
            this.error.set('Failed to load earnings statistics');
            this.loading.set(false);
          }
        });
  }

  // ────────────────────────────────────────────────
  // Helpers
  // ────────────────────────────────────────────────
  getPeriodLabel(period: PeriodKey): string {
    return this.periodLabels()[period] || period;
  }

  getLevelData(periodData: EarningsStatisticsPeriodData, level: number): EarningsStatisticsLevelData {
    return periodData.levels[level] || { com_card: 0, com_lic: 0, total: 0 };
  }

  getTotalForPeriod(periodData: EarningsStatisticsPeriodData): {
    com_card: number;
    com_lic: number;
    pool_payout: number;
    total: number;
  } {
    const totals = { com_card: 0, com_lic: 0, pool_payout: 0, total: 0 };

    Object.values(periodData.levels).forEach(levelData => {
      totals.com_card += levelData.com_card;
      totals.com_lic += levelData.com_lic;
      totals.total += levelData.total;
    });

    totals.pool_payout = periodData.pool_payout || 0;
    totals.total += totals.pool_payout;

    return totals;
  }

  formatAmount(amount: number): string {
    return amount.toFixed(2);
  }

  getStatusClass(count: number): string {
    if (count === 0) return 'text-muted';
    if (count < 50) return 'text-primary';
    if (count < 100) return 'text-primary';
    return 'text-primary fw-bold';
  }
}